import * as THREE from "three";
import { getSceneState } from "../../stores/sceneStore";
import type { BodyUUID } from "../../types/scene";
import { buildBodyParentMap } from "../../utils/bodyHierarchy";
import type { BodyRecord } from "./types";

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function projectOntoPlane(v: THREE.Vector3, normalUnit: THREE.Vector3): THREE.Vector3 {
  return v.clone().sub(normalUnit.clone().multiplyScalar(v.dot(normalUnit)));
}

export function resolveTopmostAncestorBodyUuid(
  scene: ReturnType<typeof getSceneState>,
  start: BodyUUID
): BodyUUID | null {
  try {
    const bodies = (scene?.bodies || {}) as BodyRecord;
    if (!start || !bodies?.[start]) return null;
    const parentMap = buildBodyParentMap(bodies);
    const seen = new Set<string>();
    let current: BodyUUID | null = start;
    while (current && !seen.has(String(current))) {
      seen.add(String(current));
      const parent = parentMap[current] ?? null;
      if (!parent) return current;
      current = parent as BodyUUID;
    }
    return start;
  } catch {
    return null;
  }
}

export function computePlacedInTrayNow(args: {
  cubePosScene: THREE.Vector3;
  trayBox: THREE.Box3 | null;
}): boolean {
  const { cubePosScene, trayBox } = args;
  if (!trayBox) return false;
  const min = trayBox.min;
  const max = trayBox.max;
  const eps = 0.015; // tolerate small bounds/mesh jitter near tray rim
  // Scene axes: X/Z on the ground plane, Y up.
  const inXZ =
    cubePosScene.x >= min.x - eps &&
    cubePosScene.x <= max.x + eps &&
    cubePosScene.z >= min.z - eps &&
    cubePosScene.z <= max.z + eps;
  if (!inXZ) return false;
  // Must be at/below tray top (avoid counting above-tray hovering as "placed").
  const yTop = max.y + 0.008;
  if (cubePosScene.y > yTop) return false;
  return true;
}
